"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";

/**
 * REDLINER — tmavě skleněný „dif" panel reklamačního řádu.
 * // TODO: texty klauzulí jsou ilustrativní placeholdery — nahradit reálným zněním.
 */
const clauses = [
  { label: "§ 1 Vymezení pojmů", active: false },
  { label: "§ 2 Povinnost prohlídky zboží", active: true },
  { label: "§ 3 Záruční doby", active: false },
  { label: "§ 4 Odpovědnost", active: false },
  { label: "§ 5 Řešení sporů", active: false },
];

const keyboardChips = ["⌘K", "⌘↵", "⌘D", "⌘E"];

export function RedlinerPanel({ className }: { className?: string }) {
  const [paused, setPaused] = useState(false);

  return (
    <div
      role="group"
      aria-label="Náhled reklamačního řádu: neutrální zákonná formulace přepsaná na formulaci klienta"
      tabIndex={0}
      data-paused={paused ? "true" : "false"}
      onClick={() => setPaused((value) => !value)}
      className={cn("redliner redliner-hoverable", className)}
    >
      {/* Title bar */}
      <div className="flex items-center gap-3 border-b border-[var(--color-hairline)] px-4 py-3">
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="size-2 rounded-full bg-[rgba(255,255,255,0.14)]" />
          <span className="size-2 rounded-full bg-[rgba(255,255,255,0.14)]" />
          <span className="size-2 rounded-full bg-[rgba(255,255,255,0.14)]" />
        </span>
        <span className="mono-label truncate text-text-muted">reklamacni-rad-b2b.docx</span>
        <span className="mono-label ml-auto rounded-full border border-[var(--color-hairline)] px-2.5 py-1 text-blue">
          Návrh
        </span>
        <span className="mono-label hidden text-text-faint sm:inline">Revize 2 / 7 dní</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr]">
        {/* Clause list */}
        <ul className="hidden border-r border-[var(--color-hairline)] p-2 md:block">
          {clauses.map((clause) => (
            <li key={clause.label}>
              <span
                data-active={clause.active ? "true" : "false"}
                className={cn(
                  "redliner-clause block rounded-md px-3 py-2 pl-4 text-[13px] leading-tight",
                  clause.active ? "text-text" : "text-text-muted",
                )}
              >
                {clause.label}
              </span>
            </li>
          ))}
        </ul>

        {/* Diff area */}
        <div className="flex min-h-[180px] flex-col gap-4 p-5 md:min-h-[248px]">
          <div>
            <span className="mono-label text-text-faint">OZ · neutrální zákonná formulace</span>
            <p className="mt-2 text-[14px] leading-relaxed text-text-faint line-through">
              Vady je třeba vytknout bez zbytečného odkladu poté, co je kupující mohl zjistit.
            </p>
          </div>

          <div className="relative">
            <span className="mono-label text-signal">VAŠE FORMULACE</span>
            <div className="relative mt-2 pr-6">
              <p className="redliner-motion text-[15px] leading-relaxed text-text">
                Vady zjevné při převzetí oznamuje odběratel nejpozději do 2 pracovních dnů;
                později k nim nelze přihlédnout.
              </p>
              <span
                aria-hidden="true"
                className="redliner-cursor absolute top-0 h-full w-0.5 bg-signal"
              />
            </div>
            <span className="mono-label mt-3 inline-flex items-center gap-2 rounded-md border border-[var(--color-hairline)] px-3 py-1.5 text-text-muted">
              ⌘↵ Převzít do řádu
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex flex-wrap items-center gap-3 border-t border-[var(--color-hairline)] px-4 py-3">
        <span className="mono-label text-text-faint">
          ↑↓ klauzule · ⌘↵ převzít · ⌘D porovnat s OZ
        </span>
        <span className="ml-auto flex items-center gap-2" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 16 16">
            <rect width="16" height="16" rx="4" fill="#0B1220" />
            <path
              d="M6.1 11.4c-.4-.4-.6-.9-.6-1.6 0-.3 0-.5.1-.8h1.3c-.1.2-.1.4-.1.6 0 .4.1.7.3.9.2.2.5.3.9.3.4 0 .7-.1.9-.3.2-.2.3-.5.3-.8 0-.3-.1-.5-.2-.7-.2-.2-.4-.3-.7-.5l-.7-.3c-.7-.3-1.1-.6-1.4-1-.3-.4-.4-.8-.4-1.4 0-.7.2-1.3.7-1.7.4-.4 1-.6 1.7-.6.7 0 1.2.2 1.6.5.4.4.6.9.6 1.5 0 .3 0 .5-.1.8h-1.3c.1-.2.1-.4.1-.6 0-.3-.1-.6-.2-.7-.2-.2-.4-.3-.7-.3-.3 0-.6.1-.8.3-.2.2-.3.4-.3.7 0 .3.1.5.2.6.2.2.4.4.7.5l.7.3c.7.3 1.1.6 1.4 1 .3.4.5.9.5 1.4 0 .8-.3 1.4-.8 1.8-.5.4-1.1.6-1.9.6-.7 0-1.3-.2-1.8-.5Z"
              fill="#1A56DB"
            />
          </svg>
          {/* Keyboard chips — vizuální, nikdy funkční */}
          <span className="flex gap-1" aria-hidden="true">
            {keyboardChips.map((chip) => (
              <span
                key={chip}
                className="mono-label rounded border border-[var(--color-hairline)] px-1.5 py-0.5 text-text-faint"
              >
                {chip}
              </span>
            ))}
          </span>
        </span>
      </div>
    </div>
  );
}
